import type { Category, ConsentChoice, SiteTexts, SiteTheme } from '@cmp/shared';
import { CATEGORIES } from '@cmp/shared';

/**
 * Banner UI rendered into a shadow root so site CSS cannot break it and vice versa.
 * Layer 1: title, description, Accept all / Reject all / Settings with equal visual weight.
 * Layer 2: per-category toggles and Save.
 * Badge: persistent button to reopen, so withdrawal is as easy as consent.
 */

export interface UiHandlers {
  onAcceptAll(): void;
  onRejectAll(): void;
  onSave(choice: ConsentChoice): void;
}

export interface UiOptions {
  texts: SiteTexts;
  theme: SiteTheme;
  privacyUrl?: string;
  cookieDeclarationUrl?: string;
  showBadge: boolean;
  /** In opt-out regimes the badge reads "Your privacy choices". */
  optout: boolean;
  /** Free plan: muted "Powered by" link inside the card. */
  branding?: boolean;
  /** A GPC opt-out was honoured and the law requires saying so visibly. */
  gpcHonored?: boolean;
}

export interface NoticeHandlers {
  onObject(): void;
  onDismiss(): void;
}

import { CSS } from './css';

export class BannerUi {
  private host: HTMLElement;
  private root: ShadowRoot;
  private wrap!: HTMLDivElement;
  private card!: HTMLDivElement;
  private badge!: HTMLButtonElement;
  private layer: 1 | 2 | 'notice' = 1;
  private noticeHandlers: NoticeHandlers | null = null;
  private choice: ConsentChoice = { necessary: true, functionality: false, analytics: false, marketing: false };
  private lastFocus: Element | null = null;

  constructor(private opts: UiOptions, private handlers: UiHandlers) {
    this.host = document.createElement('div');
    this.host.setAttribute('data-cmp', 'ignore');
    this.host.id = 'cmp-host';
    this.root = this.host.attachShadow({ mode: 'open' });
    this.build();
    (document.body || document.documentElement).appendChild(this.host);
  }

  private build(): void {
    const t = this.opts.theme;
    const style = document.createElement('style');
    style.textContent =
      `:host{--font:${t.font};--text:${t.text};--muted:${t.muted};--bg:${t.background};--primary:${t.primary};--on-primary:${t.onPrimary};--radius:${t.radius}}` + CSS;
    this.root.appendChild(style);

    this.wrap = document.createElement('div');
    this.wrap.className = `wrap ${t.position} hidden`;
    this.card = document.createElement('div');
    this.card.className = 'card';
    this.card.setAttribute('role', 'dialog');
    this.card.setAttribute('aria-modal', 'true');
    this.card.setAttribute('aria-labelledby', 'cmp-title');
    this.card.setAttribute('aria-describedby', 'cmp-desc');
    this.card.tabIndex = -1;
    this.wrap.appendChild(this.card);
    this.root.appendChild(this.wrap);

    this.badge = document.createElement('button');
    this.badge.className = 'badge hidden';
    this.badge.type = 'button';
    this.badge.textContent = this.opts.gpcHonored ? (this.opts.texts.gpcHonored ?? 'Opt-out request honored') : this.opts.optout ? this.opts.texts.privacyChoices : this.opts.texts.manage;
    this.badge.addEventListener('click', () => this.show(2));
    this.root.appendChild(this.badge);

    this.wrap.addEventListener('keydown', (e) => this.onKey(e));
  }

  show(layer: 1 | 2 = 1, current?: ConsentChoice): void {
    if (current) this.choice = { ...current };
    this.layer = layer;
    this.render();
    this.open(true);
  }

  /** UK statistical exception: one line, analytics already running, an Object link. Not a dialog. */
  showNotice(handlers: NoticeHandlers): void {
    this.noticeHandlers = handlers;
    this.layer = 'notice';
    this.render();
    this.open(false);
  }

  private open(focus: boolean): void {
    this.wrap.classList.toggle('l1', this.layer === 1);
    this.wrap.classList.toggle('notice', this.layer === 'notice');
    this.card.setAttribute('role', this.layer === 'notice' ? 'region' : 'dialog');
    if (this.layer === 'notice') this.card.removeAttribute('aria-modal');
    else this.card.setAttribute('aria-modal', 'true');
    this.lastFocus = document.activeElement;
    this.wrap.classList.remove('hidden');
    this.badge.classList.add('hidden');
    if (focus) {
      const first = this.card.querySelector<HTMLElement>('button, input');
      (first ?? this.card).focus();
    }
    this.reserveSpace();
  }

  hide(): void {
    this.wrap.classList.add('hidden');
    if (this.opts.showBadge) this.badge.classList.remove('hidden');
    if (this.layer !== 'notice' && this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    document.documentElement.style.scrollPaddingBottom = '';
  }

  /** Keep focused elements from ending up behind the card (WCAG 2.2, focus not obscured). */
  private reserveSpace(): void {
    if (this.opts.theme.position === 'center') return;
    const h = this.card.getBoundingClientRect().height;
    if (h > 0) document.documentElement.style.scrollPaddingBottom = `${Math.round(h) + 32}px`;
  }

  showBadgeOnly(): void {
    if (this.opts.showBadge) this.badge.classList.remove('hidden');
  }

  /** Whether a browser opt-out currently stands (California shows it); changes if the visitor later allows tracking. */
  setGpcHonored(on: boolean): void {
    this.opts.gpcHonored = on;
    this.badge.textContent = on ? (this.opts.texts.gpcHonored ?? 'Opt-out request honored') : this.opts.optout ? this.opts.texts.privacyChoices : this.opts.texts.manage;
  }

  /** Turn the floating button on after start-up (the page turned out to have no footer to hold the link). */
  enableBadge(): void {
    this.opts.showBadge = true;
    if (!this.isOpen()) this.badge.classList.remove('hidden');
  }

  /** A short, self-dismissing confirmation above the badge's spot. Used once per visitor. */
  toast(text: string): void {
    const el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    el.textContent = `✓ ${text}`;
    this.root.appendChild(el);
    setTimeout(() => el.classList.add('out'), 5000);
    setTimeout(() => el.remove(), 5600);
  }

  isOpen(): boolean {
    return !this.wrap.classList.contains('hidden');
  }

  private render(): void {
    const t = this.opts.texts;
    this.card.replaceChildren();
    if (this.layer === 'notice') {
      const p = document.createElement('p');
      p.id = 'cmp-desc';
      p.textContent = t.notice;
      const row = document.createElement('div');
      row.className = 'row';
      row.append(
        this.button('OK', true, () => this.noticeHandlers?.onDismiss()),
        this.button(t.object, false, () => this.noticeHandlers?.onObject(), true),
        this.button(t.settings, false, () => this.show(2), true),
      );
      this.card.append(p, row);
      this.card.appendChild(this.footer());
      return;
    }
    const h = document.createElement('h2');
    h.id = 'cmp-title';
    h.textContent = t.title;
    const p = document.createElement('p');
    p.id = 'cmp-desc';
    p.textContent = t.description;
    this.card.append(h, p);

    if (this.layer === 2) {
      if (this.opts.gpcHonored) {
        const s = document.createElement('p');
        s.className = 'gpc';
        s.textContent = `✓ ${t.gpcHonored ?? 'Opt-out request honored'}`;
        this.card.appendChild(s);
      }
      for (const c of CATEGORIES) this.card.appendChild(this.categoryRow(c));
    }

    const row = document.createElement('div');
    row.className = 'row';
    row.append(
      this.button(t.acceptAll, true, () => this.handlers.onAcceptAll()),
      this.button(t.rejectAll, true, () => this.handlers.onRejectAll()),
    );
    if (this.layer === 1) row.appendChild(this.button(t.settings, false, () => this.show(2), true));
    else row.appendChild(this.button(t.save, false, () => this.handlers.onSave({ ...this.choice, necessary: true })));
    this.card.appendChild(row);
    this.card.appendChild(this.footer());
  }

  /** Privacy links and, on the free plan, the powered-by line. */
  private footer(): HTMLElement {
    const t = this.opts.texts;
    const links = document.createElement('div');
    links.className = 'links';
    if (this.opts.privacyUrl) links.appendChild(this.link(t.privacyPolicy, this.opts.privacyUrl));
    if (this.opts.cookieDeclarationUrl) links.appendChild(this.link(t.cookieDeclaration, this.opts.cookieDeclarationUrl));
    if (this.opts.branding) {
      const a = this.link(`${t.poweredBy} Flat Consent`, 'https://flatconsent.com/?ref=banner');
      a.className = 'pb';
      a.target = '_blank';
      links.appendChild(a);
    }
    if (!links.childElementCount) links.classList.add('hidden');
    return links;
  }

  private categoryRow(c: Category): HTMLElement {
    const t = this.opts.texts.categories[c];
    const row = document.createElement('div');
    row.className = 'cat';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = `cmp-${c}`;
    input.checked = c === 'necessary' ? true : this.choice[c];
    input.disabled = c === 'necessary';
    input.addEventListener('change', () => {
      if (c !== 'necessary') this.choice[c] = input.checked;
    });
    const label = document.createElement('label');
    label.htmlFor = input.id;
    const strong = document.createElement('strong');
    strong.textContent = t.name;
    const span = document.createElement('span');
    span.textContent = t.description;
    label.append(strong, span);
    row.append(input, label);
    if (c === 'necessary') {
      const always = document.createElement('span');
      always.className = 'always';
      always.textContent = this.opts.texts.always;
      row.appendChild(always);
    }
    return row;
  }

  private button(text: string, primary: boolean, onClick: () => void, textStyle = false): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = text;
    if (primary) b.className = 'primary';
    else if (textStyle) b.className = 'text';
    b.addEventListener('click', onClick);
    return b;
  }

  private link(text: string, href: string): HTMLAnchorElement {
    const a = document.createElement('a');
    a.href = href;
    a.textContent = text;
    a.rel = 'noopener';
    return a;
  }

  /** Trap focus inside the dialog; Escape returns from layer 2 to layer 1 only. */
  private onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.layer === 2) {
      e.preventDefault();
      this.show(1);
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(this.card.querySelectorAll<HTMLElement>('button, input:not([disabled]), a[href]'));
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = this.root.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/** Placeholder rendered inside a blocked iframe via srcdoc, with a button that grants the category. */
export function framePlaceholder(texts: SiteTexts, theme: SiteTheme): (frame: HTMLIFrameElement, category: string) => void {
  return (frame, category) => {
    const html = `<!doctype html><html><head><meta charset="utf-8"><style>
html,body{margin:0;height:100%;font-family:${theme.font};background:#f3f4f6;color:${theme.text}}
div{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:16px;text-align:center;font-size:14px}
button{font:inherit;font-weight:600;padding:10px 16px;border-radius:8px;border:0;background:${theme.primary};color:${theme.onPrimary};cursor:pointer;min-height:44px}
</style></head><body><div><span>${escapeHtml(texts.blockedEmbed)}</span><button type="button" onclick="parent.postMessage({cmp:'grant',category:'${category}'},'*')">${escapeHtml(texts.loadEmbed)}</button></div></body></html>`;
    frame.setAttribute('srcdoc', html);
  };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);
}
